import { getAppInfo } from '@nike/get-app-info';
import getWebShellProductOptions from '@nike/shop-apis/web-shell-client/product/get-options';
import getWebShellClientLocale from '@nike/shop-apis/utils/get-webshellclient-locale';
import queryClient from '@nike/shop-apis/utils/query-client';

const miniPDPTrack = custom => {
  const track = window?.webShellClient?.analytics?.track;
  const locale = getWebShellClientLocale();
  const country = locale.country;
  const language = locale.language;
  const appInfo = getAppInfo({ version: process.env.APP_VERSION });

  const { platform, viewChannel, domain, environment, viewName, version } = appInfo;

  const site = 'nikecom';

  // Properties common for all miniPDP events
  const baseEventData = {
    locale: { country, language },
    app: {
      domain,
      environment,
      version,
      backendPlatform: platform,
      name: viewName,
    },
    version,
    view: {
      experienceType: site,
      pageName: `${site}>${viewChannel}>${viewName}`,
      pageType: viewChannel,
      pageDetail: appInfo.viewName, // should be cart to distinguish from pdp
    },
  };

  const eventData = {
    ...baseEventData,
    ...custom,
  };

  track(custom.eventName, eventData, {
    $schema: custom.$schema,
    writeKey: process.env.SEGMENT_WRITE_KEY,
  });
};

const SEGMENT_EVENTS_SCHEMA_PREFIX = `https://${process.env.NEXT_PUBLIC_HOST_NAME}/assets/measure/schemas/digital-product/dotcom/platform/web/classification`;

// Product Size Selected Event
// https://github.com/nike-internal/uip.event-registry/blob/main/event-definitions/digital-product/dotcom/platform/web/classification/experience-event/experience/pdp/event-type/track/event-name/product-size-selected/properties.tsv
const PRODUCT_SIZE_SELECTED_EVENT_SCHEMA = `${SEGMENT_EVENTS_SCHEMA_PREFIX}/experience-event/experience/pdp/event-type/track/event-name/product-size-selected/version/LATEST/schema.json`;

export const trackProductSizeSelected = ({ size, skuId, publishType, inventoryLevel }) =>
  miniPDPTrack({
    eventName: 'Product Size Selected',
    clickActivity: `size selection: ${size}`,
    $schema: PRODUCT_SIZE_SELECTED_EVENT_SCHEMA,
    products: [
      {
        sku: skuId,
        inventoryLevel,
        inventoryStatus: 'in stock', // Assuming in stock for simplicity
      },
    ],
    publishType,
  });

// Product Image Viewed Event
// https://github.com/nike-internal/uip.event-registry/blob/main/event-definitions/digital-product/dotcom/platform/web/classification/experience-event/experience/pdp/event-type/track/event-name/product-image-viewed/properties.tsv
const PRODUCT_IMAGE_VIEWED_EVENT_SCHEMA = `${SEGMENT_EVENTS_SCHEMA_PREFIX}/experience-event/experience/pdp/event-type/track/event-name/product-image-viewed/version/LATEST/schema.json`;

export const trackProductImageViewed = ({ imageIndex, isVideo }) =>
  miniPDPTrack({
    eventName: 'Product Image Viewed',
    clickActivity: 'product image viewed',
    $schema: PRODUCT_IMAGE_VIEWED_EVENT_SCHEMA,
    imageIndex,
    isVideo,
  });

// 	Select Size Clicked Event
// https://github.com/nike-internal/uip.event-registry/blob/main/event-definitions/digital-product/dotcom/platform/web/classification/experience-event/experience/wishlist/event-type/track/event-name/select-size-clicked/properties.tsv
const SELECT_SIZE_CLICKED_EVENT_SCHEMA = `${SEGMENT_EVENTS_SCHEMA_PREFIX}/experience-event/experience/wishlist/event-type/track/event-name/select-size-clicked/version/LATEST/schema.json`;

export const trackSelectSizeClicked = ({ productId, productCode }) => {
  const { queryKey } = getWebShellProductOptions({ productCode });
  // Per specs/hydration-spec.md, `merchProductId` is the hydration field equivalent
  // to cart's `productId` UUID. The fired-on-click ordering means the WebShell
  // capability may not have resolved yet, so fall back to the line-item productId
  // passed by the caller (always present on cart items, optional on wishlist v2).
  const { internalPid: pid, merchProductId: cachedProductId } =
    queryClient.getQueryData(queryKey) || {};
  return miniPDPTrack({
    eventName: 'Select Size Clicked',
    $schema: SELECT_SIZE_CLICKED_EVENT_SCHEMA,
    products: [
      {
        productId: cachedProductId || productId,
        prodigyProductId: pid,
      },
    ],
  });
};
